import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, Timer, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Field, inputCls, primaryBtn } from "@/components/AuthShell";
import { AssessmentAlertsPopup } from "@/components/assessment/AssessmentAlerts";

const CreateClass = ({ open, setOpen, onCreated }) => {
  const [name, setName] = useState("");
  const [mins, setMins] = useState(30);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await http.post("/classes", { name, min_minutes: Number(mins) });
      toast.success(`Class created · code ${r.data.join_code}`);
      setName(""); setOpen(false); onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] rounded-3xl" data-testid="create-class-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#2C4A3E]">New class</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Field label="Class name"><input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grade 7 Science" className={inputCls} data-testid="create-class-name-input" /></Field>
          <Field label="Daily attendance minimum (minutes)"><input type="number" min={1} max={480} required value={mins} onChange={(e) => setMins(e.target.value)} className={inputCls} data-testid="create-class-minutes-input" /></Field>
          <button type="submit" className={primaryBtn} data-testid="create-class-submit-btn">Create class</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function TeacherDashboard() {
  const { profile } = useProfile();
  const [classes, setClasses] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => http.get("/classes").then((r) => setClasses(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="teacher-dashboard">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">Teacher space</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2">Welcome, {profile.name}</h1>
          <p className="text-stone-500 mt-2">Guide your learners' growth in knowledge and awareness.</p>
          <AssessmentAlertsPopup storageKey="cl_assess_alerts_teacher" />
        </div>
        <button onClick={() => setOpen(true)} data-testid="create-class-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C87D53] text-white hover:bg-[#B36C44] transition-colors">
          <Plus className="w-4 h-4" /> New class
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12" data-testid="class-list">
        {classes?.map((c, i) => (
          <Link key={c.id} to={`/teacher/classes/${c.id}`} data-testid={`class-card-${c.id}`} style={{ animationDelay: `${i * 0.05}s` }}
            className="group rise rounded-3xl p-6 bg-[#F7F3EB] border border-[#E6DEC3] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300">
            <div className="flex justify-between">
              <span className="font-mono text-sm px-3 py-1 rounded-full bg-white border border-[#E6DEC3] text-[#2C4A3E] tracking-widest">{c.join_code}</span>
              <ArrowUpRight className="w-5 h-5 text-stone-400 group-hover:text-[#C87D53] transition-colors" />
            </div>
            <h3 className="font-display text-2xl text-[#2C4A3E] mt-6">{c.name}</h3>
            <div className="flex gap-5 mt-4 text-sm text-stone-500">
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" />{c.student_count} students</span>
              <span className="inline-flex items-center gap-1.5"><Timer className="w-4 h-4" />{c.min_minutes} min/day</span>
            </div>
          </Link>
        ))}
      </div>
      {classes && !classes.length && (
        <div className="text-center py-20" data-testid="class-list-empty">
          <p className="font-display text-2xl text-[#2C4A3E]">Create your first class</p>
          <p className="text-stone-500 mt-2">Students join with the class code you share.</p>
        </div>
      )}
      <CreateClass open={open} setOpen={setOpen} onCreated={load} />
    </div>
  );
}
